export class CacheUtil {
  private static cache: Map<string, { data: any; expiry: number }> = new Map();

  /**
   * Entity cache key constants - centralized for cache invalidation on language change
   */
  public static readonly ENTITY_CACHE_KEYS = {
    MINISTRY: 'ministryContent',
    EVENT: 'eventContent',
    SCHOOL: 'schoolContent',
    PRODUCT: 'productContent'
  } as const;

  /**
   * Set a value in the cache with an optional expiry time.
   * @param key The key to store the value under.
   * @param data The data to store.
   * @param ttl Time-to-live in milliseconds (optional).
   */
  static set<T>(key: string, data: T, ttl: number = 3600000 * 24): void {
    const expiry = ttl ? Date.now() + ttl : Infinity;
    this.cache.set(key, { data, expiry });
  }

  /**
   * Get a value from the cache.
   * @param key The key to retrieve the value for.
   * @returns The cached data, or null if not found or expired.
   */
  static get<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (!cached) return null;

    if (cached.expiry < Date.now()) {
      this.cache.delete(key);
      return null;
    }

    return cached.data;
  }

  /**
   * Remove a value from the cache.
   * @param key The key to remove.
   */
  static delete(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Clear all values from the cache.
   */
  static clear(): void {
    this.cache.clear();
  }

  /**
   * Delete all cache entries that match a key prefix.
   * Useful for clearing all language-specific variations of an entity cache.
   * @param prefix The key prefix to match (e.g., 'schoolContent' matches 'schoolContent_en', 'schoolContent_ar')
   */
  static deleteByPrefix(prefix: string): number {
    let deletedCount = 0;
    const keysToDelete: string[] = [];

    this.cache.forEach((_, key) => {
      if (key.startsWith(prefix)) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => {
      this.cache.delete(key);
      deletedCount++;
    });

    return deletedCount;
  }

  /**
   * Get all current cache keys (useful for debugging)
   */
  static getKeys(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * Clear all entity caches (ministries, events, schools, products)
   * Used when language changes to ensure fresh data is loaded
   */
  static clearAllEntityCaches(): void {
    // Clear exact match keys
    this.delete(this.ENTITY_CACHE_KEYS.MINISTRY);
    this.delete(this.ENTITY_CACHE_KEYS.EVENT);

    // Clear prefix-based keys (for language/type-suffixed caches)
    this.deleteByPrefix(this.ENTITY_CACHE_KEYS.SCHOOL);
    this.deleteByPrefix(this.ENTITY_CACHE_KEYS.PRODUCT);
  }
}